import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Calculator, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ScopePicker } from "@/components/ripeness/value/ScopePicker";
import { BudgetsTab } from "@/components/ripeness/value/BudgetsTab";
import { LocationTab } from "@/components/ripeness/value/LocationTab";
import { WeightsTab } from "@/components/ripeness/value/WeightsTab";
import { CalibrationTab } from "@/components/ripeness/value/CalibrationTab";
import { PreviewBar } from "@/components/ripeness/value/PreviewBar";
import { ValueAuditList } from "@/components/ripeness/ValueCalibrationPanel";
import { buildPatch, countPatch, deepClone } from "@/components/ripeness/value/valuePatch";

const TABS = [["budgets", "Budgets"], ["location", "Where we are"], ["weights", "What matters to us"], ["calibration", "Calibration"], ["audit", "Audit history"]];
const V = "/v1/ripeness/value";

/** Lead-value assumptions (lead-value-2.0) — global defaults or one client's set, three tabs + calibration + audit. */
export default function QaValueAssumptionsPage() {
    const [params, setParams] = useSearchParams();
    const clientId = params.get("client_id") || "";
    const [tab, setTab] = useState("budgets");
    const [clients, setClients] = useState([]);
    const [set, setSet] = useState(null);
    const [cal, setCal] = useState(null);
    const [globalCal, setGlobalCal] = useState(null);
    const [audit, setAudit] = useState([]);
    const [countries, setCountries] = useState(null);
    const [inherited, setInherited] = useState(null);
    const [baseline, setBaseline] = useState(null);
    const [areas, setAreas] = useState([]);
    const [draft, setDraft] = useState(null);
    const [reason, setReason] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [busy, setBusy] = useState(false);
    const previewSeq = useRef(0);
    const q = useMemo(() => (clientId ? { client_id: clientId } : {}), [clientId]);

    const load = useCallback(async () => {
        try {
            const [s, c, a, co] = await Promise.all([
                api.get(`${V}/assumptions`, { params: q }), api.get(`${V}/calibration`, { params: q }),
                api.get(`${V}/assumptions/audit`, { params: { ...q, limit: 300 } }), api.get(`${V}/countries`, { params: q }),
            ]);
            setSet(s.data); setCal(c.data); setAudit(a.data.items || []); setCountries(co.data);
            setDraft(deepClone(s.data.assumptions)); setPreview(null); setBaseline(null);
            if (clientId) {
                const [gc, gs] = await Promise.all([api.get(`${V}/calibration`), api.get(`${V}/assumptions`)]);
                setGlobalCal(gc.data); setInherited(gs.data.assumptions);
            } else { setGlobalCal(null); setInherited(s.data.defaults); }
            api.get(`${V}/clients`).then((r) => setClients(r.data || [])).catch(() => {});
        } catch (e) {
            toast.error(`Value assumptions failed: ${formatApiErrorDetail(e)}`);
        }
    }, [q, clientId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get("/v1/ripeness/areas").then((r) => setAreas((r.data.items || []).map((x) => x.value))).catch(() => setAreas([]));
    }, []);
    useEffect(() => {  // current ranking (no pending change) for the weights tab
        if (tab !== "weights" || baseline || !set) return;
        api.post(`${V}/assumptions/preview`, { assumptions: {}, top: 20 }, { params: q }).then((r) => setBaseline(r.data)).catch(() => setBaseline({ ranking_after: [], has_weights: set.has_weights }));
    }, [tab, baseline, set, q]);

    const original = set?.assumptions;
    const patch = useMemo(() => (original && draft ? buildPatch(original, draft) : {}), [original, draft]);
    const changes = countPatch(patch, original, draft);
    const overridden = useMemo(() => new Set(set?.overridden_fields || []), [set]);
    const update = useCallback((fn) => setDraft((d) => { const n = deepClone(d); fn(n); return n; }), []);

    useEffect(() => {
        if (!changes) { setPreview(null); setPreviewing(false); return undefined; }
        const seq = ++previewSeq.current;
        setPreviewing(true);
        const t = setTimeout(async () => {
            try {
                const r = await api.post(`${V}/assumptions/preview`, { assumptions: patch, top: 20 }, { params: q });
                if (seq === previewSeq.current) setPreview(r.data);
            } catch (e) {
                if (seq === previewSeq.current) setPreview({ samples: [], portfolio: null, ranking_after: [], errors: [formatApiErrorDetail(e)] });
            } finally {
                if (seq === previewSeq.current) setPreviewing(false);
            }
        }, 600);
        return () => clearTimeout(t);
    }, [patch, changes, q]);

    const save = async () => {
        setBusy(true);
        try {
            const r = await api.put(`${V}/assumptions`, { assumptions: patch, reason: reason.trim() }, { params: q });
            toast.success(`Saved v${r.data.version} — ${r.data.changed_fields?.length || 0} field${r.data.changed_fields?.length === 1 ? "" : "s"} changed`);
            setReason("");
            await load();
        } catch (e) {
            toast.error(`Save rejected: ${formatApiErrorDetail(e)}`);
        } finally {
            setBusy(false);
        }
    };
    const needReason = (fallback) => {
        if (reason.trim().length >= 5) return reason.trim();
        const r = window.prompt("Reason (audited):", fallback);
        return r && r.trim().length >= 5 ? r.trim() : null;
    };
    const locks = async (lock) => {
        const r = needReason(lock ? "keep every manual value" : "let the CTA data take over");
        if (!r) return;
        setBusy(true);
        try {
            await api.post(`${V}/assumptions/locks`, { lock, keys: null, reason: r }, { params: q });
            toast.success(lock ? "Every row locked — manual values win" : "Every row released — CTA data blends in");
            await load();
        } catch (e) {
            toast.error(`Lock change failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setBusy(false);
        }
    };
    const recompute = async () => {
        setBusy(true);
        try {
            const r = await api.post(`${V}/calibration/apply`, {}, { params: q });
            toast.success(r.data.tables_changed ? `Calibration taken in — ${r.data.data_driven_rows} data-driven rows` : "Calibration up to date — nothing moved");
            await load();
        } catch (e) {
            toast.error(`Calibration failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setBusy(false);
        }
    };
    const searchIndications = useCallback(async (s) => {
        try { return (await api.get("/v1/ripeness/indications", { params: { search: s, limit: 8, sort: "count" } })).data.items || []; } catch { return []; }
    }, []);
    const setScope = (cid) => { if (changes && !window.confirm("Discard unsaved changes?")) return; setParams(cid ? { client_id: cid } : {}); };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100 p-6 pb-48" data-testid="value-assumptions-page">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                    <Link to="/qa/ripeness-radar" className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="va-back-link"><ArrowLeft className="w-3 h-3" /> Trial Ripeness Radar</Link>
                    <h1 className="text-2xl font-bold tracking-tight inline-flex items-center gap-2 mt-1"><Calculator className="w-6 h-6 text-emerald-400" /> Value assumptions</h1>
                    <p className="text-sm text-slate-400 mt-1 max-w-3xl">
                        Budget per patient = phase range × indication factor × location index, blended with CTA budgets as they accumulate (k = {set?.assumptions?.calibration?.min_samples ?? 5});
                        weights give the "value to us" that drives the ranking. Model <span className="font-mono text-slate-300">lead-value-2.0</span>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="inline-flex items-center gap-1.5 rounded border border-slate-700 bg-slate-800/40 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800" data-testid="va-reload-btn"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Reload</button>
                </div>
            </div>
            <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 mb-4">
                <ScopePicker clients={clients} value={clientId} onChange={setScope} set={set} />
                <div className="text-xs text-slate-500 mt-2" data-testid="va-scope-note">
                    {clientId ? "This client's set inherits the global defaults; amber fields are overridden here. API-key clients can edit the same set through /api/v1/ripeness/value/*." : "Every client inherits these defaults until they override a field. Country indices and the calibration threshold live here."}
                    {set ? ` · ${overridden.size} overridden field${overridden.size === 1 ? "" : "s"}` : ""}
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-1 mb-4" data-testid="va-tabs">
                {TABS.map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setTab(k)}
                        className={`rounded px-3 py-1.5 text-xs border ${tab === k ? "border-emerald-400/60 text-emerald-200 bg-emerald-400/10" : "border-slate-700 text-slate-400 hover:text-slate-200"}`} data-testid={`va-tab-${k}`}>
                        {l}{k === "audit" ? ` (${audit.length})` : ""}{k === "calibration" && cal ? ` (${cal.data_driven_rows})` : ""}
                    </button>
                ))}
            </div>
            <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 pb-6">
                {!set || !draft ? <div className="text-xs text-slate-500" data-testid="va-loading">Loading…</div> : null}
                {set && draft && tab === "budgets" ? <BudgetsTab draft={draft} update={update} overridden={overridden} cal={cal} areas={areas} isGlobal={!clientId} searchIndications={searchIndications} inherited={inherited} /> : null}
                {set && draft && tab === "location" ? <LocationTab draft={draft} update={update} overridden={overridden} countries={countries?.countries} source={countries?.source} isGlobal={!clientId} locBefore={set.location_index?.value} /> : null}
                {set && draft && tab === "weights" ? <WeightsTab draft={draft} update={update} overridden={overridden} areas={areas} preview={changes ? preview : baseline} searchIndications={searchIndications} hasWeights={set.has_weights} /> : null}
                {set && tab === "calibration" ? <CalibrationTab cal={cal} globalCal={globalCal} isGlobal={!clientId} busy={busy} onRecompute={recompute} onLockAll={() => locks(true)} onReleaseAll={() => locks(false)} locks={set.assumptions.locks} /> : null}
                {set && tab === "audit" ? <ValueAuditList items={audit} /> : null}
            </div>
            {set && draft ? <PreviewBar changes={changes} preview={preview} previewing={previewing} reason={reason} setReason={setReason} onSave={save} onDiscard={() => { setDraft(deepClone(original)); setReason(""); }} saving={busy} hasWeights={set.has_weights} /> : null}
        </div>
    );
}
